import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface PageTransitionProps {
  children: React.ReactNode;
}

export default function PageTransition({ children }: PageTransitionProps): React.JSX.Element {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showContent, setShowContent] = useState(true);
  const prevPathRef = useRef(location.pathname);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    // Start transition
    setIsTransitioning(true);
    setShowContent(false);
    setProgress(0);

    // Animate progress bar
    let currentProgress = 0;
    clearProgressTimer();
    progressTimerRef.current = setInterval(() => {
      currentProgress += Math.random() * 30 + 10;
      if (currentProgress >= 90) {
        currentProgress = 90;
        clearProgressTimer();
      }
      setProgress(currentProgress);
    }, 80);

    // Complete transition
    const completeTimer = setTimeout(() => {
      clearProgressTimer();
      setProgress(100);
      setShowContent(true);

      // Hide progress bar after completion
      setTimeout(() => {
        setIsTransitioning(false);
        setProgress(0);
      }, 300);
    }, 250);

    return () => {
      clearTimeout(completeTimer);
      clearProgressTimer();
    };
  }, [location.pathname, clearProgressTimer]);

  return (
    <>
      {/* Top progress bar */}
      {isTransitioning && (
        <div className="page-progress-bar">
          <div
            className="page-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Content with fade transition */}
      <div
        className={`page-transition-content ${showContent ? 'page-visible' : 'page-hidden'}`}
      >
        {children}
      </div>
    </>
  );
}
